<template>
  <v-dialog v-model="isDialogVisible" persistent max-width="720px">
    <v-card>
      <v-card-title class="d-flex align-center py-2 px-4">
        <span class="subtitle-1 font-weight-medium">Custom Recurrence</span>
        <span class="caption grey--text ml-2">(CST Timezone)</span>
        <v-spacer></v-spacer>
        <v-btn icon small @click="closeDialog">
          <v-icon>{{ icons.mdiClose }}</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pt-4 pb-0">
        <v-row>
          <!-- LEFT: identity + destination + filename -->
          <v-col cols="12" md="7" class="pr-md-4">
            <v-row dense>
              <v-col cols="12">
                <v-text-field
                  v-model="editableRowItem.description"
                  v-uppercase=globalSettingItem.conEverythingToCaps
                  label="Cron Job Name"
                  dense
                  autofocus
                  @blur="somethingChanged"
                  @keydown.enter="somethingChanged"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="12">
                <template v-if="presetDestination">
                  <div class="caption grey--text">Report</div>
                  <div class="body-2 mb-2">{{ presetDestinationLabel }}</div>
                </template>
                <v-autocomplete
                  v-else
                  v-model="editableRowItem.destination"
                  label="Cron Job Destination"
                  :items="destinations"
                  item-text="text"
                  item-value="value"
                  dense
                  clearable
                  @blur="somethingChanged"
                  @keydown.enter="somethingChanged"
                ></v-autocomplete>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="12">
                <v-text-field
                  v-model="editableRowItem.outputFilename"
                  label="Output Filename"
                  dense
                  clearable
                  @blur="somethingChanged"
                  @keydown.enter="somethingChanged"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-col>

          <!-- RIGHT: schedule + active toggle -->
          <v-col cols="12" md="5">
            <v-sheet outlined rounded class="pa-3">
              <div class="caption grey--text mb-3">Schedule (CST)</div>
              <v-row dense>
                <v-col cols="6">
                  <v-select
                    v-model="editableRowItem.expectedRunTime.frequency"
                    label="Repeat"
                    :items="frequencies"
                    dense
                    @change="somethingChanged"
                  ></v-select>
                </v-col>
                <v-col cols="6">
                  <v-select
                    v-if="isMonthly"
                    v-model="editableRowItem.expectedRunTime.monthDay"
                    label="Day of month"
                    :items="monthDays"
                    dense
                    @change="somethingChanged"
                  ></v-select>
                  <v-text-field
                    v-else
                    v-model="editableRowItem.expectedRunTime.days"
                    label="Days (*, 0-7, or range e.g. 1-5)"
                    :rules="[cronDaysRule]"
                    dense
                    @focus="$event.target.select()"
                    @blur="somethingChanged"
                    @keydown.enter="somethingChanged"
                  ></v-text-field>
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model="editableRowItem.expectedRunTime.hrs"
                    label="Hrs"
                    type="number"
                    dense
                    @focus="$event.target.select()"
                    @keydown.tab="somethingChanged"
                    @keydown.enter="somethingChanged"
                  ></v-text-field>
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model="editableRowItem.expectedRunTime.mins"
                    label="Mins"
                    type="number"
                    dense
                    @focus="$event.target.select()"
                    @blur="somethingChanged"
                    @keydown.enter="somethingChanged"
                  ></v-text-field>
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model="editableRowItem.expectedRunTime.secs"
                    label="Secs"
                    type="number"
                    dense
                    @focus="$event.target.select()"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-divider class="my-2"></v-divider>
              <!-- Segmented: the switch showed only the state it was in, so a job that was
                   Inactive read as the word "Inactive" beside a knob with nothing to compare
                   it to. Writes the same flag and still marks the dialog dirty. -->
              <BinaryToggle
                class="mt-2"
                id="expectedTimeDialogActiveToggle"
                aria-label="Job state: inactive or active"
                off-label="Inactive"
                on-label="Active"
                :value="editableRowItem.isActiveInactive"
                @input="val => { editableRowItem.isActiveInactive = val; somethingChanged(); }"
                @keydown.enter.native="somethingChanged" />
            </v-sheet>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions class="px-4 pb-3 pt-2">
        <v-spacer></v-spacer>
        <v-btn color="blue darken-1" text @click="closeDialog">CLOSE</v-btn>
        <v-btn color="blue darken-1" @click="save">SAVE</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  mapActions, mapMutations, mapGetters, mapState,
} from 'vuex';
import { mdiClose } from '@mdi/js';
import BinaryToggle from '../common/base/BinaryToggle.vue';
import { amountToNumber } from '../../util/shared/vue-global';
import { normalizeCronDays, isValidCronDays } from '../../util/shared/cronDays';
import { FREQUENCIES, MONTH_DAYS, normalizeMonthDay } from '../../util/shared/cronSchedule';
import GlobalToCaps from '../../util/shared/GlobalToCaps.mixin.vue';

export default {
  name: 'ExpectedTimeDialog',
  components: { BinaryToggle },
  mixins: [GlobalToCaps],
  data() {
    return {
      icons: { mdiClose },
      frequencies: FREQUENCIES,
      monthDays: MONTH_DAYS,
      destinations: [
        // { text: 'Penalty Cron Job', value: 'taxPenaltyCal' },
        { text: 'Tax Adjustment by Date (PDF Report)', value: 'taxAdjByDate' },
        { text: 'Payment Adjustments by Type (PDF Report)', value: 'paymentAdjByType' },
        { text: 'Payment - Tax Records with Balance after Tax credit (PDF Report)', value: 'paymentTaxBalAfterCredit' },
        { text: 'Mailing Address Changes Summary (PDF Report)', value: 'mailingAddressChangesSummary' },
        { text: 'Taxroll Changes Summary (PDF Report)', value: 'taxrollChangesSummary' },
        { text: 'Paid Tax Warrants (PDF Report)', value: 'paidTaxWarrants' },
        { text: 'Payment Paid List (CSV Report)', value: 'printPaymentList' },
        { text: 'OD Positive Pay ARP (Fixed Width Report)', value: 'odPositivePayArp' },
        { text: 'WAR Positive Pay ARP (Fixed Width Report)', value: 'warPositivePayArp' },
        { text: 'WAR Positive Pay ARP With All Year (Fixed Width Report)', value: 'warPositivePayArpWithAllYear' },
        { text: 'Payroll Positive Pay ARP (Fixed Width Report)', value: 'payrollPositivePayArp' },
        { text: 'Payment Tax Details Export (CSV Export)', value: 'paymentTaxDetailsExport' },
      ],
    };
  },
  props: {
    closeDialogBox: {
      type: Boolean,
      default: false,
    },
    baseCrudKey: {
      type: String,
      default: '',
    },
    rowItem: {
      type: Object,
      default: () => ({}),
    },
    presetDestination: {
      type: String,
      default: '',
    },
    presetParams: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    ...mapGetters('base/crud', [
      'item',
      'items',
      'index',
      'isVisible',
    ]),
    ...mapState({
      globalSettingItem: (state) => state.SystemConfig.globalSettingItem || {},
    }),
    presetDestinationLabel() {
      const match = this.destinations.find((d) => d.value === this.presetDestination);
      return match ? match.text : this.presetDestination;
    },
    isMonthly() {
      // An absent frequency reads as weekly — every pre-existing legacy job doc.
      const rt = (this.editableRowItem || {}).expectedRunTime || {};
      return rt.frequency === 'monthly';
    },
    isDialogVisible: {
      get() {
        return this.closeDialogBox;
      },
      set(value) {
        this.$emit('settingDialogVal', value);
      },
    },
    editableRowItem: {
      get() {
        const item = JSON.parse(JSON.stringify(this.rowItem));
        // Legacy docs carry no frequency/monthDay. Fill them in so the cadence selects have a
        // value to display, without changing what the job does (absent frequency = weekly).
        const rt = item.expectedRunTime || {};
        item.expectedRunTime = {
          ...rt,
          frequency: rt.frequency === 'monthly' ? 'monthly' : 'weekly',
          days: normalizeCronDays(rt.days),
          monthDay: normalizeMonthDay(rt.monthDay),
        };
        return item;
      },
      set(value) {
        this.$emit('update:rowItem', value);
      },
    },
  },
  watch: {
    closeDialogBox(val) {
      if (val && this.presetDestination) {
        const item = { ...this.editableRowItem };
        item.destination = this.presetDestination;
        item.params = { ...this.presetParams };
        this.$emit('update:rowItem', item);
        this.setItem([this.baseCrudKey, item]);
      }
    },
  },
  methods: {
    ...mapActions('cron/cronJobUtil', ['upsertCronJob', 'getCronJobs']),
    ...mapMutations('base/crud', [
      'setCriteria',
      'setItems',
      'setItem',
      'setIndex',
    ]),
    closeDialog() {
      this.$emit('settingDialogVal', false);
      this.$nextTick(() => {
        this.setIndex([this.baseCrudKey, -1]);
      });
    },
    cronDaysRule(v) {
      return isValidCronDays(v)
        || 'Use *, a day 0-7, an ascending range like 1-5, or a list like 1,3,5';
    },
    async save() {
      const item = { ...this.editableRowItem };
      const { expectedRunTime } = item;
      if (expectedRunTime.frequency === 'monthly') {
        // monthDay is 'L'/'LW' or a day string — like days, never run it through amountToNumber.
        expectedRunTime.monthDay = normalizeMonthDay(expectedRunTime.monthDay);
        delete expectedRunTime.days; // a monthly job must not persist a stale weekday token
      } else {
        expectedRunTime.frequency = 'weekly';
        expectedRunTime.days = normalizeCronDays(expectedRunTime.days);
        if (!isValidCronDays(expectedRunTime.days)) return; // invalid; the field rule shows why
        delete expectedRunTime.monthDay;
      }
      expectedRunTime.hrs = amountToNumber(expectedRunTime.hrs);
      expectedRunTime.mins = amountToNumber(expectedRunTime.mins);
      expectedRunTime.secs = amountToNumber(expectedRunTime.secs);
      delete item.nextExpectedRunTime;
      await this.upsertCronJob({
        item,
        baseCrudKey: this.baseCrudKey,
      }).then(() => {
        this.closeDialog();
      });
    },

    somethingChanged() {
      this.$emit('update:rowItem', this.editableRowItem);
      this.setItem([this.baseCrudKey, this.editableRowItem]);
    },
  },
};
</script>
